import { Injectable } from '@angular/core';
import {
  TopupAccount,
  TopupAccountDto,
  TopupAccountDetail,
  TopupAccountDetailDto,
  TopupTransaction,
  TopupTransactionDto,
  TopupHold,
  TopupHoldDto
} from '../../models/cityparking/topup.model';
import { PagedResultDto } from '../../models/table.model';

@Injectable({
  providedIn: 'root',
})
export class TopupMapper {
  
  // ================= Normal Functions =================

  /**
   * Parse a date string into a Date object safely
   */
  private parseDate(val: string | null | undefined): Date | null {
    if (!val) return null;
    const parsed = new Date(val);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Normalizes a TopupAccount DTO into UI Model
   */
  mapAccount(dto: TopupAccountDto): TopupAccount {
    const raw = dto as TopupAccountDto & Record<string, any>;
    return {
      id: dto.id ?? raw['Id'] ?? 0,
      topupNumber: dto.topupNumber ?? raw['TopupNumber'] ?? '',
      amount: dto.amount ?? raw['Amount'] ?? 0,
      fullName: dto.fullName ?? raw['FullName'] ?? '',
      phone: dto.phone ?? raw['Phone'] ?? '',
      createDate: this.parseDate(dto.createDate ?? raw['CreateDate']),
      canAdjustBalance: dto.canAdjustBalance ?? raw['CanAdjustBalance'] ?? false
    };
  }

  /**
   * Maps table response for Accounts
   */
  mapAccountTable(dto: any): PagedResultDto<TopupAccount> {
    const raw = dto as Record<string, any>;
    const list = (dto.data ?? raw['Data'] ?? []) as TopupAccountDto[];
    const total = dto.total ?? raw['Total'] ?? 0;
    
    return {
      data: list.map(item => this.mapAccount(item)),
      total: total
    };
  }

  /**
   * Normalizes detail account view
   */
  mapAccountDetail(dto: TopupAccountDetailDto): TopupAccountDetail {
    const raw = dto as TopupAccountDetailDto & Record<string, any>;
    const txsDto = (dto.recentTransactions ?? raw['RecentTransactions'] ?? []) as TopupTransactionDto[];
    return {
      ...this.mapAccount(dto),
      recentTransactions: txsDto.map(tx => this.mapTransaction(tx))
    };
  }

  /**
   * Normalizes a Transaction DTO
   */
  mapTransaction(dto: TopupTransactionDto): TopupTransaction {
    const raw = dto as TopupTransactionDto & Record<string, any>;
    return {
      id: dto.id ?? raw['Id'] ?? 0,
      topupAccountId: dto.topupAccountId ?? raw['TopupAccountId'] ?? null,
      topupNumber: dto.topupNumber ?? raw['TopupNumber'] ?? '',
      fullName: dto.fullName ?? raw['FullName'] ?? '',
      phone: dto.phone ?? raw['Phone'] ?? '',
      paidDate: this.parseDate(dto.paidDate ?? raw['PaidDate']),
      amount: dto.amount ?? raw['Amount'] ?? 0,
      balance: dto.balance ?? raw['Balance'] ?? null,
      topupMethod: dto.topupMethod ?? raw['TopupMethod'] ?? null,
      description: dto.description ?? raw['Description'] ?? '',
      remark: dto.remark ?? raw['Remark'] ?? '',
      transactionNo: dto.transactionNo ?? raw['TransactionNo'] ?? '',
      referenceCode: dto.referenceCode ?? raw['ReferenceCode'] ?? '',
      feeName: dto.feeName ?? raw['FeeName'] ?? '',
      isPendingApproval: dto.isPendingApproval ?? raw['IsPendingApproval'] ?? false
    };
  }

  /**
   * Maps table response for Transactions
   */
  mapTransactionTable(dto: any): PagedResultDto<TopupTransaction> {
    const raw = dto as Record<string, any>;
    const list = (dto.data ?? raw['Data'] ?? []) as TopupTransactionDto[];
    const total = dto.total ?? raw['Total'] ?? 0;

    return {
      data: list.map(item => this.mapTransaction(item)),
      total: total
    };
  }

  /**
   * Normalizes a Hold DTO
   */
  mapHold(dto: TopupHoldDto): TopupHold {
    const raw = dto as TopupHoldDto & Record<string, any>;
    return {
      id: dto.id ?? raw['Id'] ?? 0,
      topupAccountId: dto.topupAccountId ?? raw['TopupAccountId'] ?? null,
      name: dto.name ?? raw['Name'] ?? '',
      amount: dto.amount ?? raw['Amount'] ?? 0,
      remark: dto.remark ?? raw['Remark'] ?? '',
      createDate: this.parseDate(dto.createDate ?? raw['CreateDate']),
      status: dto.status ?? raw['Status'] ?? false
    };
  }

  /**
   * Maps table response for Holds
   */
  mapHoldTable(dto: any): PagedResultDto<TopupHold> {
    const raw = dto as Record<string, any>;
    const list = (dto.data ?? raw['Data'] ?? []) as TopupHoldDto[];
    const total = dto.total ?? raw['Total'] ?? 0;

    return {
      data: list.map(item => this.mapHold(item)),
      total: total
    };
  }
}
